'use client';
import { useNotifications } from '@/api/queries/notifications.queries';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/logo';
import { useSidebarSafe } from '@/components/ui/sidebar';
import { UnlockCta } from '@/features/freemium/components/unlock-cta';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useHeaderCtaStore } from '@/shared/stores/header-cta-store';
import { cn } from '@/shared/utils/formatting';
import { getChoosePlanHrefForCreator } from '@/shared/utils/routes';
import { Bell, Menu, Search } from 'lucide-react';
import { TourAnchor, tourAnchor } from '@/shared/lib/tours/anchors';
import { AI_CHATS_PATTERN } from '@/shared/lib/chrome/route-classes';
import { setAppNavOverlayOpen } from './app-nav-overlay-store';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CreateMenu } from './create-menu';
import { HeaderAiShortcut } from './header-ai-shortcut';
import { UserMenu } from './UserMenu';

const SmartSearchDropdown = dynamic(
  () =>
    import('@/components/search/SmartSearchDropdown').then((m) => ({
      default: m.SmartSearchDropdown,
    })),
  { ssr: false }
);

const MobileSearch = dynamic(
  () =>
    import('@/components/ui/mobile-search').then((m) => ({
      default: m.MobileSearch,
    })),
  { ssr: false }
);

function getHeaderBackground({
  onboardingMode,
  transparentUntilScroll,
  isScrolled,
  sidebarOpen,
}: {
  onboardingMode: boolean;
  transparentUntilScroll: boolean;
  isScrolled: boolean;
  sidebarOpen: boolean;
}) {
  if (onboardingMode) return 'bg-transparent backdrop-blur-xs';
  if (transparentUntilScroll && (isScrolled || sidebarOpen))
    return 'bg-background/95 backdrop-blur-md';
  if (transparentUntilScroll) return 'bg-transparent';
  return 'bg-background backdrop-blur-xs';
}

export function TopHeader({
  classNameDivContainer,
  hiddenSearch,
  hiddenGetStarted,
  onboardingMode = false,
  transparentUntilScroll = false,
}: {
  classNameDivContainer?: string;
  hiddenSearch?: boolean;
  hiddenGetStarted?: boolean;
  onboardingMode?: boolean;
  transparentUntilScroll?: boolean;
}) {
  const { isAuthenticated, logout } = useAuthStore();
  const unlockCtaHandler = useHeaderCtaStore((s) => s.unlockCtaHandlers.at(-1));
  const sidebar = useSidebarSafe();

  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Sidebar-open state is cookie-seeded on the client but the prerendered
  // shell is cookie-neutral (closed) — reading sidebar.open before hydration
  // mismatches for members. First render mirrors the server; the pre-paint
  // CSS guard (globals.css §Pre-hydration chrome) covers the visual window.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);
  const sidebarOpen = hydrated && !!sidebar?.open;
  const searchPrimerRef = useRef<HTMLInputElement>(null);

  // Warm the lazy MobileSearch chunk so its input mounts (and takes focus from
  // the primer) with minimal delay after the tap — see the search button.
  useEffect(() => {
    void import('@/components/ui/mobile-search');
  }, []);

  useEffect(() => {
    if (!transparentUntilScroll) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [transparentUntilScroll]);
  const { data: notificationsData } = useNotifications();

  // Server-side unread total (counts beyond the fetched page, FRONT-244);
  // fall back to counting the page when the envelope lacks it.
  const unreadCount = useMemo(() => {
    if (typeof notificationsData?.unread_total === 'number') {
      return notificationsData.unread_total;
    }
    const items = notificationsData?.notifications ?? [];
    return items.filter((n) => n && !n.read_at).length;
  }, [notificationsData]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <header
        data-slot="top-header"
        style={{ top: 'var(--impersonate-offset, 0px)' }}
        className={cn(
          'sticky left-0 right-0 h-16 z-9999 transition-colors duration-300 overflow-visible',
          getHeaderBackground({
            onboardingMode,
            transparentUntilScroll,
            isScrolled,
            sidebarOpen,
          })
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between h-full px-4 relative z-10',
            classNameDivContainer
          )}
        >
          {/* Left: Hamburger + Logo */}
          <div className="flex items-center">
            {!onboardingMode && sidebar && (
              <div className="flex items-center justify-center shrink-0">
                <button
                  onClick={() => {
                    // /ai's sidebar is the Chats sidebar — ☰ opens the app nav
                    // over it instead. Read at click time, never at render.
                    if (AI_CHATS_PATTERN.test(window.location.pathname)) setAppNavOverlayOpen(true);
                    else sidebar.toggleSidebar();
                  }}
                  className="mr-4 rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Toggle menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </div>
            )}

            <Logo size="md" linkTo={isAuthenticated ? '/' : '/creators'} />
          </div>

          {/* Center: Search */}
          {!hiddenSearch && (
            <div
              data-slot="header-search"
              className="flex-1 max-w-xl mx-4 hidden sm:block"
              {...tourAnchor(TourAnchor.HEADER_SEARCH)}
            >
              <SmartSearchDropdown />
            </div>
          )}

          {/* Right: Notifications, User. One control grid (YouTube's masthead
              rule): every control is a 40px box, icons 24px in round buttons /
              20px in pills, spacing only from this gap — no per-item margins.
              Below sm the boxes drop to 36px with no gap: four 40px controls
              plus logo and ☰ overflow a 360px phone. */}
          <div className="flex items-center sm:gap-2">
            {/* Mobile search trigger - visible only below sm */}
            {!hiddenSearch && (
              <>
                {/* Focused synchronously in the tap so mobile raises the
                    keyboard within the user gesture; the lazy overlay then
                    transfers focus to its own input once mounted, keeping the
                    keyboard up. Programmatic focus after the async overlay mount
                    can't summon the keyboard on its own (FRONT-201). */}
                <input
                  ref={searchPrimerRef}
                  type="text"
                  inputMode="search"
                  tabIndex={-1}
                  aria-hidden="true"
                  // text-base (16px) avoids iOS auto-zoom while the primer holds focus.
                  className="sr-only text-base"
                />
                <button
                  data-slot="header-search"
                  {...tourAnchor(TourAnchor.HEADER_SEARCH)}
                  onClick={() => {
                    searchPrimerRef.current?.focus();
                    setIsMobileSearchOpen(true);
                  }}
                  className="sm:hidden flex size-9 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Search"
                >
                  <Search className="size-6" />
                </button>
              </>
            )}

            {isAuthenticated && onboardingMode && (
              <button
                onClick={handleLogout}
                className="text-sm text-white/70 hover:text-white transition-colors flex items-center gap-1.5"
              >
                Sign out
              </button>
            )}

            {isAuthenticated && !onboardingMode && (
              <>
                {/* Taboo Terra took this slot back from the tester-only Messages
                    shortcut (FRONT-307), which stays in the studio header. */}
                <HeaderAiShortcut />

                {/* Create Menu */}
                <CreateMenu />

                {/* Notifications */}
                <Link
                  href="/notifications"
                  className="relative flex size-9 sm:size-10 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <span className="sr-only">Notifications</span>
                  <Bell className="size-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0 sm:-right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-2xs font-semibold text-white bg-red-primary rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                      <span className="sr-only"> unread</span>
                    </span>
                  )}
                </Link>

                {/* User Menu */}
                <UserMenu />
              </>
            )}

            {!isAuthenticated && (
              <div className="flex items-center gap-2">
                <Link href="/sign-in">
                  <Button variant="ghost" size="sm">
                    Sign In
                  </Button>
                </Link>

                {!hiddenGetStarted &&
                  (unlockCtaHandler !== undefined ? (
                    <UnlockCta
                      label="Unlock everything"
                      source="top_header"
                      handler={unlockCtaHandler}
                      className="hidden sm:inline-flex"
                      href={getChoosePlanHrefForCreator(unlockCtaHandler)}
                    />
                  ) : (
                    <Link href="/choose-plan" className="hidden sm:block">
                      <Button size="sm">Get Started</Button>
                    </Link>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Immersive gradient fade — Netflix-style top darkening */}
        {transparentUntilScroll && !isScrolled && !sidebarOpen && (
          <div
            data-slot="header-scrim"
            className="absolute left-0 right-0 top-0 h-28 pointer-events-none"
            style={{
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.25) 50%, transparent 100%)',
            }}
          />
        )}
      </header>

      {/* Mobile search overlay */}
      {!hiddenSearch && (
        <MobileSearch isOpen={isMobileSearchOpen} onClose={() => setIsMobileSearchOpen(false)} />
      )}
    </>
  );
}
